/**
 * Modelo canónico de reserva + (de)serialización tolerante.
 *
 * Port de `rider_app/lib/data/models/reserve_model.dart`. A diferencia del
 * ride, la respuesta REST de reservas (`ReserveResponseDto`) también usa
 * `server_phase` (no `phase`) para el estado, y NO trae `fares`.
 */
import { asMap, remoteStatusValue, toStr } from './raw';
import { normalizeReserveStatus } from './status';

export interface ReserveRtdb {
  id: string | null;
  rideType: string | null;
  date: string | null;
  paymentMethod: string | null;
  /** Estado ya normalizado al conjunto canónico. */
  status: string | null;
  fares: string | null;
  dropoffAddress: string | null;
  pickupAddress: string | null;
  riderId: string | null;
  riderName: string | null;
  riderPhone: string | null;
  createdAt: string | null;
}

function emptyReserve(id: string | null = null): ReserveRtdb {
  return {
    id,
    rideType: null,
    date: null,
    paymentMethod: null,
    status: null,
    fares: null,
    dropoffAddress: null,
    pickupAddress: null,
    riderId: null,
    riderName: null,
    riderPhone: null,
    createdAt: null,
  };
}

/**
 * Deserializa un nodo RTDB `Reserves/{reserveId}`.
 * Si el valor es null, devuelve una reserva con solo `id` (paridad con
 * `ReserveModel.fromSnapshot`, que conserva `snapshot.key`).
 */
export function reserveFromSnapshot(key: string | null, value: unknown): ReserveRtdb {
  if (value === null || value === undefined) {
    return emptyReserve(key);
  }
  const data = asMap(value) ?? {};

  return {
    id: key,
    rideType: toStr(data['ride_type']),
    date: toStr(data['date']),
    paymentMethod: toStr(data['payment_method']),
    status: normalizeReserveStatus(remoteStatusValue(data)),
    fares: toStr(data['fares']),
    dropoffAddress: toStr(data['dropoff_address']),
    pickupAddress: toStr(data['pickup_address']),
    riderId: toStr(data['rider_id']),
    riderName: toStr(data['rider_name']),
    riderPhone: toStr(data['rider_phone']),
    createdAt: toStr(data['created_at']),
  };
}

/**
 * Deserializa la respuesta REST NestJS (`ReserveResponseDto`). `pickup`/`dropoff`
 * llegan como `{lat,lng,address}`; el estado se normaliza desde `server_phase`
 * (o `status`). No trae `fares`.
 */
export function reserveFromRest(json: Record<string, unknown>): ReserveRtdb {
  const pickup = asMap(json['pickup']);
  const dropoff = asMap(json['dropoff']);

  return {
    ...emptyReserve(),
    id: toStr(json['id']),
    rideType: toStr(json['ride_type']),
    date: toStr(json['scheduled_at']),
    paymentMethod: toStr(json['payment_method']),
    status: normalizeReserveStatus(remoteStatusValue(json)),
    dropoffAddress: toStr(dropoff?.['address']),
    pickupAddress: toStr(pickup?.['address']),
    riderId: toStr(json['rider_id']),
    riderName: toStr(json['rider_name']),
    riderPhone: toStr(json['rider_phone']),
    createdAt: toStr(json['created_at']),
  };
}

/** Mapa que se ESCRIBE en RTDB (paridad con `ReserveModel.toFirebaseMap`). */
export function reserveToFirebaseMap(reserve: ReserveRtdb): Record<string, unknown> {
  const map: Record<string, unknown> = {
    ride_type: reserve.rideType,
    date: reserve.date,
    payment_method: reserve.paymentMethod,
    status: reserve.status,
    fares: reserve.fares,
    dropoff_address: reserve.dropoffAddress,
    pickup_address: reserve.pickupAddress,
  };
  if (reserve.riderId !== null) map['rider_id'] = reserve.riderId;
  if (reserve.riderName !== null) map['rider_name'] = reserve.riderName;
  if (reserve.riderPhone !== null) map['rider_phone'] = reserve.riderPhone;
  if (reserve.createdAt !== null) map['created_at'] = reserve.createdAt;
  return map;
}

export function normalizedReserveStatus(reserve: ReserveRtdb): string | null {
  return normalizeReserveStatus(reserve.status);
}

export function isReservePending(reserve: ReserveRtdb): boolean {
  return normalizedReserveStatus(reserve) === 'pending';
}

export function isReserveConfirmed(reserve: ReserveRtdb): boolean {
  const s = normalizedReserveStatus(reserve);
  return s === 'confirmed' || s === 'accepted';
}

export function isReserveCancelled(reserve: ReserveRtdb): boolean {
  return normalizedReserveStatus(reserve) === 'cancelled';
}

export function isReserveCompleted(reserve: ReserveRtdb): boolean {
  return normalizedReserveStatus(reserve) === 'completed';
}
